import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createVNode as _createVNode, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "modal-mask" }
const _hoisted_2 = { class: "modal alarm-modal" }
const _hoisted_3 = { class: "modal-hdr" }
const _hoisted_4 = { class: "modal-body scrollable" }
const _hoisted_5 = { class: "section-block" }
const _hoisted_6 = { class: "control-row" }
const _hoisted_7 = {
  key: 0,
  class: "slider-wrapper"
}
const _hoisted_8 = { class: "control-row mt-4" }
const _hoisted_9 = {
  key: 1,
  class: "slider-wrapper"
}
const _hoisted_10 = { class: "section-block" }
const _hoisted_11 = { class: "row-right" }
const _hoisted_12 = { class: "value-text" }
const _hoisted_13 = {
  key: 0,
  class: "expanded-section"
}
const _hoisted_14 = { class: "notification-options" }
const _hoisted_15 = { class: "notification-option" }
const _hoisted_16 = { class: "section-block" }
const _hoisted_17 = { class: "row-right" }
const _hoisted_18 = { class: "value-text text-truncate" }
const _hoisted_19 = {
  key: 0,
  class: "expanded-section"
}
const _hoisted_20 = { class: "alarm-log-list" }
const _hoisted_21 = {
  key: 0,
  class: "no-logs"
}
const _hoisted_22 = { class: "log-details" }
const _hoisted_23 = { class: "log-message" }
const _hoisted_24 = { class: "log-time" }

import { ref, onMounted, watch, computed } from 'vue';
import axios from '@/axiosInstance';
import { useToast } from 'vue-toast-notification';
import PermissionRequestModal from '@/components/PermissionRequestModal.vue';
import DualRangeSlider from '@/components/ui/DualRangeSlider.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'EnvironmentConfigModal',
  props: {
    deviceId: {}
  },
  emits: ['close', 'saved'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;
const toast = useToast();

const saving = ref(false);

// Local state
const tempEnabled = ref(false);
const humEnabled = ref(false);

const tempRange = ref<[number, number]>([10, 40]); // Default fallback
const humRange = ref<[number, number]>([30, 70]); // Default fallback

// Expandable sections state
const showAlarmPush = ref(false);
const showAlarmLog = ref(false);

// Notification preferences
const notifyEmail = ref(false);

// Permission Modal State
const showPermissionModal = ref(false);


// Alarm log (mock data for now)
const alarmLog = ref([
    { type: 'temp', message: 'Temperature exceeded 40°C', timestamp: new Date(Date.now() - 3600000) },
    { type: 'hum', message: 'Humidity below 30%', timestamp: new Date(Date.now() - 7200000) },
    { type: 'temp', message: 'Temperature recovered to normal', timestamp: new Date(Date.now() - 10800000) },
]);

// Computed properties
const getNotificationSummary = computed(() => {
    return notifyEmail.value ? 'Email' : 'None';
});

const alarmLogCount = computed(() => alarmLog.value.length);

function formatLogTime(timestamp: Date): string {
    return new Date(timestamp).toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

// Load current settings
onMounted(async () => {
  try {
    const { data } = await axios.get(`/devices/${props.deviceId}/get-graph`);
    if (data) {
        if (data.temp_min != null && data.temp_max != null) {
            tempEnabled.value = true;
            tempRange.value = [data.temp_min, data.temp_max];
        } else {
            tempEnabled.value = false;
        }

        if (data.hum_min != null && data.hum_max != null) {
            humEnabled.value = true;
            humRange.value = [data.hum_min, data.hum_max];
        } else {
            humEnabled.value = false;
        }

        notifyEmail.value = !!data.notify_email;
    }
  } catch (e) {
    console.error("Errore caricamento settings:", e);
    toast.error("Impossibile caricare le impostazioni attuali");
  }
});

async function save() {
  saving.value = true;
  try {
     const payload = {
        temp_min: tempEnabled.value ? tempRange.value[0] : null,
        temp_max: tempEnabled.value ? tempRange.value[1] : null,
        hum_min: humEnabled.value ? humRange.value[0] : null,
        hum_max: humEnabled.value ? humRange.value[1] : null,
        notify_email: notifyEmail.value,
     };

     await axios.post(`/devices/${props.deviceId}/thresholds`, payload);
     
     toast.success("Impostazioni salvate con successo", { duration: 2500, position: "top-right" });
     emit('saved');
     emit('close');
  } catch (e: any) {
    // Check for permission error
    const isPermissionError = e.response?.status === 401 || (e.response?.data?.message && e.response.data.message.includes("Non hai i permessi"));

    if (isPermissionError) {
        // Show toast - extract full message from backend
        let msg = "Non hai i permessi";
        const responseData = e.response?.data;
        if (typeof responseData === 'string') {
            msg = responseData;
        } else if (responseData?.message) {
            msg = responseData.message;
        } else if (responseData?.details) {
            msg = responseData.details;
        } else if (responseData?.error) {
            msg = responseData.error;
        }
        
        toast.error(msg, { position: "top-right", duration: 4000 });

        // Do NOT close the config modal yet, otherwise this component unmounts 
        // and PermissionRequestModal (which is a child) won't show.
        // We delay the permission modal to read the toast.
        setTimeout(() => {
            showPermissionModal.value = true;
        }, 2500);
        return;
    }

    // Extract error message
    const msg = e.response?.data?.message || e.response?.data?.details || e.message || "Error saving settings";
    const finalMsg = typeof msg === 'object' ? JSON.stringify(msg) : msg;

    // Close modal immediately
    emit('close');

    // Show toast with delay
    setTimeout(() => {
        toast.error(finalMsg, { duration: 4000, position: "top-right" }); // Slightly longer duration for readabilty
    }, 300);
  } finally {
    saving.value = false;
  }
}

function onPermissionModalClose() {
    showPermissionModal.value = false;
    emit('close'); // Close the config modal too when permission flow ends
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("button", {
            class: "ico-btn",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close'))),
            title: "Close without saving"
          }, [...(_cache[8] || (_cache[8] = [
            _createElementVNode("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              width: "24",
              height: "24",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              "stroke-width": "2",
              "stroke-linecap": "round",
              "stroke-linejoin": "round"
            }, [
              _createElementVNode("path", { d: "m15 18-6-6 6-6" })
            ], -1)
          ]))]),
          _cache[10] || (_cache[10] = _createElementVNode("h3", null, "Alarm Settings", -1)),
          _createElementVNode("button", {
            class: "ico-btn",
            onClick: save,
            title: "Save and close"
          }, [...(_cache[9] || (_cache[9] = [
            _createElementVNode("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              width: "24",
              height: "24",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              "stroke-width": "2",
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              class: "lucide lucide-check"
            }, [
              _createElementVNode("path", { d: "M20 6 9 17l-5-5" })
            ], -1)
          ]))])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _cache[23] || (_cache[23] = _createElementVNode("p", { class: "subtitle" }, "Temperature and humidity alarm switches and ranges can be set.", -1)),
          _createElementVNode("section", _hoisted_5, [
            _cache[15] || (_cache[15] = _createElementVNode("h4", { class: "sec-title" }, "Alarm settings", -1)),
            _createElementVNode("div", _hoisted_6, [
              _cache[12] || (_cache[12] = _createElementVNode("span", { class: "control-label" }, "Temperature Upper and Lower Limits", -1)),
              _createElementVNode("div", {
                class: _normalizeClass(["toggle-switch", { active: tempEnabled.value }]),
                onClick: _cache[1] || (_cache[1] = ($event: any) => (tempEnabled.value = !tempEnabled.value))
              }, [...(_cache[11] || (_cache[11] = [
                _createElementVNode("span", { class: "knob" }, null, -1)
              ]))], 2)
            ]),
            (tempEnabled.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createVNode(DualRangeSlider, {
                    min: -20,
                    max: 80,
                    step: 0.1,
                    modelValue: tempRange.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((tempRange).value = $event)),
                    unit: "°C"
                  }, null, 8, ["modelValue"])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_8, [
              _cache[14] || (_cache[14] = _createElementVNode("span", { class: "control-label" }, "Humidity Upper and Lower Limits", -1)),
              _createElementVNode("div", {
                class: _normalizeClass(["toggle-switch", { active: humEnabled.value }]),
                onClick: _cache[3] || (_cache[3] = ($event: any) => (humEnabled.value = !humEnabled.value))
              }, [...(_cache[13] || (_cache[13] = [
                _createElementVNode("span", { class: "knob" }, null, -1)
              ]))], 2)
            ]),
            (humEnabled.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createVNode(DualRangeSlider, {
                    min: 0,
                    max: 100,
                    step: 1,
                    modelValue: humRange.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((humRange).value = $event)),
                    unit: "%"
                  }, null, 8, ["modelValue"])
                ]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("section", _hoisted_10, [
            _cache[19] || (_cache[19] = _createElementVNode("h4", { class: "sec-title" }, "Alarm Push", -1)),
            _createElementVNode("div", {
              class: "control-row with-arrow clickable",
              onClick: _cache[5] || (_cache[5] = ($event: any) => (showAlarmPush.value = !showAlarmPush.value))
            }, [
              _cache[17] || (_cache[17] = _createElementVNode("span", { class: "control-label bold" }, "Alarm", -1)),
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("span", _hoisted_12, _toDisplayString(getNotificationSummary.value), 1),
                (_openBlock(), _createElementBlock("svg", {
                  class: _normalizeClass(["chevron", { rotated: showAlarmPush.value }]),
                  xmlns: "http://www.w3.org/2000/svg",
                  width: "20",
                  height: "20",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                }, [...(_cache[16] || (_cache[16] = [
                  _createElementVNode("path", { d: "m9 18 6-6-6-6" }, null, -1)
                ]))], 2))
              ])
            ]),
            _createVNode(_Transition, { name: "expand" }, {
              default: _withCtx(() => [
                (showAlarmPush.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, [
                        _createElementVNode("label", _hoisted_15, [
                          _withDirectives(_createElementVNode("input", {
                            type: "checkbox",
                            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((notifyEmail).value = $event))
                          }, null, 512), [
                            [_vModelCheckbox, notifyEmail.value]
                          ]),
                          _cache[18] || (_cache[18] = _createElementVNode("span", { class: "option-label" }, "Notifica Email", -1))
                        ])
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _createElementVNode("section", _hoisted_16, [
            _cache[22] || (_cache[22] = _createElementVNode("h4", { class: "sec-title" }, "Temp/Hum/Anti-tamper/Record", -1)),
            _createElementVNode("div", {
              class: "control-row with-arrow clickable",
              onClick: _cache[7] || (_cache[7] = ($event: any) => (showAlarmLog.value = !showAlarmLog.value))
            }, [
              _cache[21] || (_cache[21] = _createElementVNode("span", { class: "control-label bold" }, "Alarm_log", -1)),
              _createElementVNode("div", _hoisted_17, [
                _createElementVNode("span", _hoisted_18, _toDisplayString(alarmLogCount.value) + " events", 1),
                (_openBlock(), _createElementBlock("svg", {
                  class: _normalizeClass(["chevron", { rotated: showAlarmLog.value }]),
                  xmlns: "http://www.w3.org/2000/svg",
                  width: "20",
                  height: "20",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                }, [...(_cache[20] || (_cache[20] = [
                  _createElementVNode("path", { d: "m9 18 6-6-6-6" }, null, -1)
                ]))], 2))
              ])
            ]),
            _createVNode(_Transition, { name: "expand" }, {
              default: _withCtx(() => [
                (showAlarmLog.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                      _createElementVNode("div", _hoisted_20, [
                        (alarmLog.value.length === 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_21, " No alarm events recorded "))
                          : _createCommentVNode("", true),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(alarmLog.value, (log, index) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: index,
                            class: "log-item"
                          }, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["log-type", log.type])
                            }, _toDisplayString(log.type.toUpperCase()), 3),
                            _createElementVNode("div", _hoisted_22, [
                              _createElementVNode("div", _hoisted_23, _toDisplayString(log.message), 1),
                              _createElementVNode("div", _hoisted_24, _toDisplayString(formatLogTime(log.timestamp)), 1)
                            ])
                          ]))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ])
        ])
      ])
    ]),
    (showPermissionModal.value)
      ? (_openBlock(), _createBlock(PermissionRequestModal, {
          key: 0,
          deviceId: __props.deviceId,
          permissionType: "change_thresholds",
          onClose: onPermissionModalClose
        }, null, 8, ["deviceId"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})